import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

/**
 * Reusable phone number input with country-code selector.
 *
 * India (+91) is selected by default because our primary market is India.
 * The list is intentionally short — the top 10 countries where NextScene has
 * meaningful traffic + a few common diaspora ones. Adding a new country is
 * a one-line edit to COUNTRIES.
 *
 * Values:
 *  - `dial` is the current country dial code including the "+" ("+91")
 *  - `number` is the LOCAL number (no dial code), digits-only, capped at max length
 *
 * The parent composes the full E.164 as `${dial}${number}` when posting.
 */
export const COUNTRIES = [
  { code: "IN", dial: "+91", flag: "🇮🇳", label: "India",         max: 10 },
  { code: "US", dial: "+1",  flag: "🇺🇸", label: "United States", max: 10 },
  { code: "GB", dial: "+44", flag: "🇬🇧", label: "United Kingdom",max: 10 },
  { code: "AE", dial: "+971",flag: "🇦🇪", label: "UAE",           max: 9  },
  { code: "SG", dial: "+65", flag: "🇸🇬", label: "Singapore",     max: 8  },
  { code: "AU", dial: "+61", flag: "🇦🇺", label: "Australia",     max: 9  },
  { code: "CA", dial: "+1",  flag: "🇨🇦", label: "Canada",        max: 10 },
  { code: "NP", dial: "+977",flag: "🇳🇵", label: "Nepal",         max: 10 },
  { code: "BD", dial: "+880",flag: "🇧🇩", label: "Bangladesh",    max: 10 },
  { code: "LK", dial: "+94", flag: "🇱🇰", label: "Sri Lanka",     max: 9  },
];

export function findCountry(dial) {
  return COUNTRIES.find((c) => c.dial === dial) || COUNTRIES[0];
}

export default function PhoneInput({ dial, onDialChange, number, onNumberChange, testIdPrefix = "phone" }) {
  const country = findCountry(dial);
  return (
    <div className="mt-1 flex" data-testid={`${testIdPrefix}-wrap`}>
      <Select value={dial} onValueChange={onDialChange}>
        <SelectTrigger data-testid={`${testIdPrefix}-country`} className="w-[110px] rounded-r-none border-r-0 bg-zinc-900 border-zinc-800 focus:ring-[#DC2626] font-medium">
          <SelectValue>
            <span className="inline-flex items-center gap-1.5">
              <span className="text-base leading-none">{country.flag}</span>
              <span>{country.dial}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent
          position="popper"
          side="bottom"
          align="start"
          className="bg-[#180D0D] border-zinc-800 text-white max-h-64"
        >
          {COUNTRIES.map((c) => (
            <SelectItem key={c.code} value={c.dial} data-testid={`${testIdPrefix}-country-${c.code}`}>
              <span className="inline-flex items-center gap-2">
                <span className="text-base leading-none">{c.flag}</span>
                <span className="font-medium">{c.dial}</span>
                <span className="text-zinc-400 text-xs">{c.label}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        data-testid={`${testIdPrefix}-number`}
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={country.max}
        required
        value={number}
        onChange={(e) => onNumberChange(e.target.value.replace(/\D/g, "").slice(0, country.max))}
        placeholder={country.code === "IN" ? "98123 45678" : "Number"}
        className="rounded-l-none bg-zinc-900 border-zinc-800 focus-visible:ring-[#DC2626]"
      />
    </div>
  );
}
